import { NewsArticle } from '../services/newsService';

export const staticNewsData: NewsArticle[] = [
  {
    id: '1',
    title: 'Welcome to Trifusion AI Platform',
    summary: 'We are excited to announce the launch of our new AI-powered development platform that revolutionizes how developers build applications.',
    body: `We are thrilled to introduce Trifusion AI Platform, a groundbreaking development environment that combines the power of artificial intelligence with modern web technologies.

Our platform offers:
- AI-powered code generation and optimization
- Real-time collaboration tools
- Advanced debugging capabilities
- Seamless deployment workflows
- Comprehensive analytics and monitoring

Join thousands of developers who are already transforming their development process with Trifusion AI Platform.`,
    author: 'Trifusion Team',
    date: '2025-01-19',
    formatted_date: 'January 19, 2025'
  },
  {
    id: '2',
    title: 'New React 19 Integration Features',
    summary: 'Discover the latest React 19 features integrated into our platform, including improved server components and enhanced performance optimizations.',
    body: `React 19 brings exciting new features that we've seamlessly integrated into the Trifusion platform:

**Server Components Enhancement:**
- Improved streaming capabilities
- Better hydration performance
- Enhanced SEO optimization

**Performance Improvements:**
- Faster rendering with concurrent features
- Optimized bundle sizes
- Better memory management

**Developer Experience:**
- Enhanced debugging tools
- Improved error boundaries
- Better TypeScript integration

These updates ensure that your applications built on Trifusion are always using the latest and most efficient React patterns.`,
    author: 'Development Team',
    date: '2025-01-18',
    formatted_date: 'January 18, 2025'
  },
  {
    id: '3',
    title: 'Advanced TypeScript Support Released',
    summary: 'Enhanced TypeScript integration with advanced type checking, better IntelliSense, and improved error reporting for a superior development experience.',
    body: `We're excited to announce major improvements to our TypeScript support:

**Advanced Type Checking:**
- Stricter type inference
- Better generic type support
- Enhanced union and intersection types

**Improved Developer Tools:**
- Real-time type error detection
- Advanced auto-completion
- Intelligent code refactoring

**Performance Enhancements:**
- Faster type checking
- Optimized compilation times
- Better memory usage

These improvements make TypeScript development on Trifusion more productive and enjoyable than ever before.`,
    author: 'TypeScript Team',
    date: '2025-01-17',
    formatted_date: 'January 17, 2025'
  },
  {
    id: '4',
    title: 'Mobile-First Design System Launch',
    summary: 'Introducing our comprehensive mobile-first design system with responsive components, accessibility features, and modern UI patterns.',
    body: `Our new mobile-first design system is now available, featuring:

**Responsive Components:**
- Adaptive layouts for all screen sizes
- Touch-friendly interactions
- Optimized performance on mobile devices

**Accessibility First:**
- WCAG 2.1 AA compliance
- Screen reader optimization
- Keyboard navigation support

**Modern UI Patterns:**
- Dark mode support
- Smooth animations and transitions
- Consistent design tokens

**Developer Benefits:**
- Pre-built component library
- Comprehensive documentation
- Easy customization options

Start building beautiful, accessible applications with our new design system today.`,
    author: 'Design Team',
    date: '2025-01-16',
    formatted_date: 'January 16, 2025'
  },
  {
    id: '5',
    title: 'AI-Powered Code Review System',
    summary: 'Revolutionary AI code review system that automatically detects bugs, suggests optimizations, and ensures code quality standards.',
    body: `Introducing our AI-powered code review system that transforms how you maintain code quality:

**Intelligent Bug Detection:**
- Automatic identification of potential issues
- Security vulnerability scanning
- Performance bottleneck detection

**Smart Suggestions:**
- Code optimization recommendations
- Best practice enforcement
- Refactoring opportunities

**Quality Assurance:**
- Automated testing suggestions
- Code coverage analysis
- Documentation completeness checks

**Team Collaboration:**
- Streamlined review process
- Automated feedback generation
- Learning from team patterns

Experience faster, more thorough code reviews with AI assistance.`,
    author: 'AI Research Team',
    date: '2025-01-15',
    formatted_date: 'January 15, 2025'
  },
  {
    id: '6',
    title: 'Cloud Deployment Made Simple',
    summary: 'Simplified cloud deployment with one-click deployments, automatic scaling, and comprehensive monitoring for your applications.',
    body: `Deploy your applications to the cloud with unprecedented ease:

**One-Click Deployments:**
- Automated build and deployment pipelines
- Multiple cloud provider support
- Zero-downtime deployments

**Automatic Scaling:**
- Intelligent resource allocation
- Cost optimization
- Performance monitoring

**Comprehensive Monitoring:**
- Real-time application metrics
- Error tracking and alerting
- Performance analytics

**Security Features:**
- Automated security scanning
- SSL certificate management
- Environment variable protection

Focus on building great applications while we handle the infrastructure complexity.`,
    author: 'DevOps Team',
    date: '2025-01-14',
    formatted_date: 'January 14, 2025'
  }
];

// Helper function to get news by ID
export const getNewsById = (id: string): NewsArticle | undefined => {
  return staticNewsData.find(article => article.id === id);
};

// Helper function to search news
export const searchStaticNews = (query: string): NewsArticle[] => {
  if (!query.trim()) return staticNewsData;
  
  const lowercaseQuery = query.toLowerCase();
  return staticNewsData.filter(article => 
    article.title.toLowerCase().includes(lowercaseQuery) ||
    article.summary.toLowerCase().includes(lowercaseQuery) ||
    article.body.toLowerCase().includes(lowercaseQuery) ||
    article.author.toLowerCase().includes(lowercaseQuery)
  );
};

// Helper function to get paginated news
export const getPaginatedNews = (page: number = 1, limit: number = 6): {
  news: NewsArticle[];
  total: number;
  totalPages: number;
  hasMore: boolean;
} => {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedNews = staticNewsData.slice(startIndex, endIndex);
  const total = staticNewsData.length;
  const totalPages = Math.ceil(total / limit);
  const hasMore = page < totalPages;
  
  return {
    news: paginatedNews,
    total,
    totalPages,
    hasMore
  };
};